import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import ChartUploader from './ChartUploader';
import TradeLog from './TradeLog';
import InstrumentModal from '../Watchlist/InstrumentModal';
import { useAppData } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';

const TypeBadge = ({ type, label }) => {
  const colors = {
    Support: 'bg-app-green/20 text-app-green border-app-green/30',
    Resistance: 'bg-app-red/20 text-app-red border-app-red/30',
    Target: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    Stop: 'bg-app-red/20 text-app-red border-app-red/30',
    'Key Level': 'bg-app-accent/20 text-app-accent border-app-accent/30'
  };
  return (
    <div className="flex flex-col items-end">
      <span className={`px-2.5 py-1 text-[11px] rounded-md border uppercase font-black tracking-tight ${colors[type] || 'bg-gray-800 text-gray-300'}`}>
        {type}
      </span>
      {label && <span className="text-[9px] font-black uppercase tracking-tighter text-app-muted mt-1">{label}</span>}
    </div>
  );
};

export default function InFocusItem({ item, watchlists, onUpdate }) {
  const { saveWatchlist } = useAppData();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const selectedWatchlist = watchlists.find(w => w.id === item.watchlistId);
  const instrument = selectedWatchlist?.instruments.find(i => i.id === item.instrumentId);

  const formatCurrency = (val, currency = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(parseFloat(val) || 0);
    } catch (e) {
      return val;
    }
  };

  const hasImage = item.imageBase64 || item.chartUrl;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 flex gap-3">
          <div className="w-1/3 space-y-1.5">
            <label className="text-xs font-black uppercase tracking-widest text-app-muted ml-1">Watchlist</label>
            <select 
              value={item.watchlistId} 
              onChange={e => onUpdate({ watchlistId: e.target.value, instrumentId: '' })}
              className="w-full text-xs bg-app-surface border-app-border font-bold uppercase tracking-widest h-12 rounded-xl"
            >
              <option value="">Select List...</option>
              {watchlists.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <div className="w-2/3 space-y-1.5">
            <label className="text-xs font-black uppercase tracking-widest text-app-muted ml-1">Instrument</label>
            <select 
              value={item.instrumentId} 
              onChange={e => onUpdate({ instrumentId: e.target.value })}
              className="w-full text-xs bg-app-surface border-app-border font-bold uppercase tracking-widest h-12 rounded-xl"
              disabled={!selectedWatchlist}
            >
              <option value="">Select Security...</option>
              {selectedWatchlist?.instruments.map(i => <option key={i.id} value={i.id}>{i.code} - {i.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col justify-end">
          <div className="flex items-center gap-4 bg-app-surface/50 px-5 py-2 rounded-xl border border-app-border h-12">
            <span className="text-xs font-black uppercase tracking-[0.1em] text-app-muted whitespace-nowrap">Carry Over</span>
            <button 
              onClick={() => onUpdate({ carryOver: !item.carryOver })}
              className={`w-12 h-6 rounded-full relative transition-all shadow-inner shrink-0 ${item.carryOver ? 'bg-app-accent' : 'bg-app-border'}`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${item.carryOver ? 'left-7' : 'left-1'}`} />
            </button>
          </div>
        </div>
      </div>

      {!instrument ? (
        <div className="text-center py-24 bg-app-bg/20 border-2 border-dashed border-app-border rounded-xl">
          <SafeIcon icon={FiIcons.FiTarget} className="mx-auto text-app-muted/10 mb-4" size={56} />
          <p className="text-app-muted text-sm font-mono uppercase tracking-[0.4em]">Select an instrument to begin planning</p>
        </div>
      ) : (
        <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-500">
          {/* Instrument Header Card */}
          <div className="bg-app-surface p-8 rounded-xl border border-app-border flex flex-col md:flex-row justify-between gap-8 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-app-accent" />
            <div className="flex-1">
              <div className="flex items-center gap-6">
                <h3 className="text-4xl font-mono text-app-accent font-black tracking-tighter">
                  {instrument.code}
                </h3>
                <span className="text-xl text-app-text font-sans font-medium opacity-40">/ {instrument.name}</span>
                <button 
                  onClick={() => setIsEditModalOpen(true)}
                  className="p-2.5 hover:bg-app-bg rounded-lg text-app-muted hover:text-app-accent transition-all"
                >
                  <SafeIcon icon={FiIcons.FiEdit} size={20} />
                </button>
              </div>
              {instrument.notes && (
                <p className="text-sm text-app-muted mt-5 max-w-2xl italic leading-relaxed border-l-2 border-app-border pl-6">
                  {instrument.notes}
                </p>
              )}
            </div>

            {instrument.priceLevels?.length > 0 && (
              <div className="flex flex-wrap gap-3 content-start md:max-w-md justify-end">
                {instrument.priceLevels.map(level => (
                  <div key={level.id} className="bg-app-bg px-4 py-3 rounded-xl border border-app-border flex items-center gap-4 shadow-inner">
                    <span className="font-mono text-xl font-black text-white">
                      {formatCurrency(level.price, instrument.currency)}
                    </span>
                    <TypeBadge type={level.type} label={level.label} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tactical Plan */}
          <div className="space-y-4">
            <label className="text-xs font-black uppercase tracking-[0.4em] text-app-muted flex items-center gap-2 mb-2 ml-1">
              <SafeIcon icon={FiIcons.FiZap} className="text-app-accent" /> Tactical Execution Plan
            </label>
            <textarea 
              value={item.todayPlan} 
              onChange={e => onUpdate({ todayPlan: e.target.value })}
              placeholder="Triggers, levels, and invalidation criteria..."
              className="w-full h-48 text-lg leading-relaxed bg-app-surface/50 border-app-border focus:border-app-accent p-8 rounded-2xl shadow-inner"
            />
          </div>

          {/* Chart Display Section */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black uppercase tracking-[0.4em] text-app-muted flex items-center gap-2 ml-1">
                <SafeIcon icon={FiIcons.FiImage} className="text-app-accent" /> Technical Analysis Chart
              </label>
            </div>

            {hasImage ? (
              <div className="space-y-4">
                <div className="relative group rounded-2xl overflow-hidden border border-app-border bg-app-surface shadow-2xl aspect-video flex items-center justify-center">
                  <img 
                    src={item.imageBase64 || item.chartUrl} 
                    alt="Analysis Chart"
                    className="max-w-full max-h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 backdrop-blur-sm">
                    <button 
                      onClick={() => onUpdate({ imageBase64: '', chartUrl: '' })}
                      className="bg-app-red text-white p-3 rounded-full hover:scale-110 transition-transform shadow-xl"
                    >
                      <FiIcons.FiTrash2 size={24} />
                    </button>
                    <a 
                      href={item.imageBase64 || item.chartUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-app-accent text-black p-3 rounded-full hover:scale-110 transition-transform shadow-xl"
                    >
                      <FiIcons.FiMaximize2 size={24} />
                    </a>
                  </div>
                </div>
                {item.chartCaption && (
                  <p className="text-center text-[10px] font-black uppercase tracking-widest text-app-muted italic">
                    {item.chartCaption}
                  </p>
                )}
              </div>
            ) : null}

            <ChartUploader 
              imageBase64={item.imageBase64}
              chartUrl={item.chartUrl}
              caption={item.chartCaption}
              onImageChange={(val) => onUpdate({ imageBase64: val, chartUrl: val ? '' : item.chartUrl })}
              onUrlChange={(val) => onUpdate({ chartUrl: val, imageBase64: val ? '' : item.imageBase64 })}
              onCaptionChange={(val) => onUpdate({ chartCaption: val })}
            />
          </div>

          {/* Trade Log */}
          <TradeLog 
            log={item.tradeLog || []} 
            setLog={(log) => onUpdate({ tradeLog: log })} 
          />
        </div>
      )}

      {isEditModalOpen && (
        <InstrumentModal 
          isOpen={true} 
          initialData={instrument} 
          onClose={() => setIsEditModalOpen(false)} 
          onSave={async (updatedInstrument) => {
            const updatedInstruments = selectedWatchlist.instruments.map(i => i.id === updatedInstrument.id ? updatedInstrument : i);
            await saveWatchlist({ ...selectedWatchlist, instruments: updatedInstruments });
          }} 
        />
      )}
    </div>
  );
}